import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { createUser, updateUser, getUserById } from '../../../services/users';
import { getAdminUnits } from '../../../services/adminUnits';
import { useAuth } from '../../../hooks/useAuth';
import Input from '../../../components/Forms/Input';
import Select from '../../../components/Forms/Select';
import Toast from '../../../components/Common/Toast';
import { 
  ArrowLeft, UserPlus, UserCheck, 
  Shield, Mail, Phone, Fingerprint, 
  Key, Loader2, Save 
} from 'lucide-react';

// Password validation helper
const validatePassword = (password) => {
  if (password.length < 6) return 'Password must be at least 6 characters long';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return null;
};

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [adminUnits, setAdminUnits] = useState([]);
  const [toast, setToast] = useState(null);

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      id_number: '',
      role: 'member',
      assigned_unit_id: '',  // single value
      password: '',
      is_active: true,
    }
  });

  const selectedRole = watch('role');

  useEffect(() => {
    loadAdminUnits();
    if (isEdit) loadUser();
  }, [id]);

  const loadAdminUnits = async () => {
    try {
      const units = await getAdminUnits(); // protected endpoint, returns units scoped to organization
      const level1Units = units.filter(u => u.level === 1);
      setAdminUnits(level1Units.map(u => ({ value: u.id, label: `${u.name} (${u.code})` })));
    } catch (err) {
      console.error('Failed to load admin units', err);
      setToast({ type: 'error', message: 'Failed to load admin units' });
    }
  };

  const loadUser = async () => {
    try {
      const userData = await getUserById(id);
      if (userData.assigned_unit_ids && userData.assigned_unit_ids.length > 0) {
        userData.assigned_unit_id = userData.assigned_unit_ids[0];
      }
      reset(userData);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to load user data' });
    }
  };

  const onSubmit = async (data) => {
    // Password validation for new user creation
    if (!isEdit && data.password) {
      const passwordError = validatePassword(data.password);
      if (passwordError) {
        setToast({ type: 'error', message: passwordError });
        return;
      }
    }
    // For edit, if a new password is provided, validate it
    if (isEdit && data.password && data.password.trim() !== '') {
      const passwordError = validatePassword(data.password);
      if (passwordError) {
        setToast({ type: 'error', message: passwordError });
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { ...data };
      if (payload.role === 'admin_unit_admin' && payload.assigned_unit_id) {
        payload.assigned_unit_ids = [payload.assigned_unit_id];
        delete payload.assigned_unit_id;
      } else {
        delete payload.assigned_unit_id;
      }
      if (isEdit) {
        // If password is empty, remove it from payload so backend doesn't update it
        if (!payload.password) delete payload.password;
        await updateUser(id, payload);
        setToast({ type: 'success', message: 'User profile updated' });
      } else {
        await createUser(payload);
        setToast({ type: 'success', message: 'User created successfully' });
      }
      setTimeout(() => navigate('/dashboard/org-admin/users'), 1500);
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'admin_unit_admin', label: 'Admin Unit Admin' },
    { value: 'member', label: 'Member' },
  ];

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
      
      {/* Header & Back Button */}
      <div className="flex flex-col gap-4">
        <button 
          onClick={() => navigate('/dashboard/org-admin/users')}
          className="group flex items-center gap-2 text-slate-400 hover:text-indigo-600 font-bold text-sm transition-colors w-fit"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to User List
        </button>
        
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-100">
            {isEdit ? <UserCheck size={24} /> : <UserPlus size={24} />}
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
              {isEdit ? 'Update User' : 'Create New User'}
            </h1>
            <p className="text-slate-500 font-medium">Manage organization access and profile identity.</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        
        {/* Card 1: Personal Information */}
        <section className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-8 flex items-center gap-2 px-1">
            Personal Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Input name="full_name" control={control} label="Full Name" rules={{ required: true }} />
            <Input name="email" control={control} label="Email Address" rules={{ required: true }} />
            <Input name="phone" control={control} label="Phone Number" />
            <Input name="id_number" control={control} label="ID Number" />
            
            {!isEdit && (
              <div className="md:col-span-2">
                <Input 
                  name="password" 
                  control={control} 
                  label="Initial Password" 
                  type="password" 
                  rules={{ required: true, minLength: 6 }} 
                />
              </div>
            )}
            {isEdit && (
              <div className="md:col-span-2">
                <Input 
                  name="password" 
                  control={control} 
                  label="New Password (optional)" 
                  type="password" 
                />
                <p className="text-[10px] text-slate-400 mt-1">Leave blank to keep current password</p>
              </div>
            )}
          </div>
        </section>

        {/* Card 2: Permissions & Access */}
        <section className="bg-slate-50 p-8 rounded-[2.5rem] border border-slate-200/60 transition-all">
          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500 mb-8 flex items-center gap-2 px-1">
            <Shield size={14} /> Permissions & Access
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <Select 
                name="role" 
                control={control} 
                label="System Role" 
                options={roleOptions} 
                rules={{ required: true }} 
              />
              <p className="text-[11px] text-slate-400 leading-relaxed italic px-1">
                Role determines the level of access to organization features and units.
              </p>
            </div>

            <Select 
              name="is_active" 
              control={control} 
              label="Account Active" 
              options={[{ value: true, label: 'Yes (Enabled)' }, { value: false, label: 'No (Disabled)' }]} 
            />

            {/* Conditional Jurisdictions - Single Select */}
            {selectedRole === 'admin_unit_admin' && (
              <div className="md:col-span-2 p-6 bg-white rounded-3xl border border-indigo-100 shadow-sm animate-in zoom-in-95 duration-300">
                <div className="flex items-center gap-2 mb-4 text-indigo-600">
                  <Save size={16} />
                  <span className="text-xs font-black uppercase tracking-widest">Assign Jurisdiction</span>
                </div>
                <Select
                  name="assigned_unit_id"
                  control={control}
                  label="Select Admin Unit"
                  options={adminUnits}
                  rules={{ required: 'At least one unit required for admin_unit_admin' }}
                />
                <p className="text-[10px] text-slate-400 mt-2">Select the monthly meeting this admin will manage.</p>
              </div>
            )}
          </div>
        </section>

        {/* Form Actions */}
        <div className="flex items-center gap-4 pt-4">
          <button 
            type="submit" 
            disabled={loading} 
            className="flex-1 md:flex-none md:min-w-[180px] bg-indigo-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <span>{isEdit ? 'Update User' : 'Register User'}</span>}
          </button>
          
          <button 
            type="button" 
            onClick={() => navigate('/dashboard/org-admin/users')}
            className="px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:bg-slate-100 transition-all"
          >
            Cancel
          </button>
        </div>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default UserForm;